/**
 * SpeakerConfirmationRequired Component
 *
 * Placeholder component shown when a feature is blocked because speakers
 * have not been confirmed. Displays a message explaining why the feature
 * is unavailable and a CTA button to open speaker confirmation.
 */

import { Users, MessageSquare, BarChart2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

type BlockedFeature = "chat" | "summary" | "comments";

interface SpeakerConfirmationRequiredProps {
  /** Which feature is being blocked */
  feature: BlockedFeature;
  /** Callback to open speaker confirmation wizard */
  onConfirmSpeakers: () => void;
}

const FEATURE_CONFIG: Record<
  BlockedFeature,
  {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  }
> = {
  chat: {
    icon: MessageSquare,
    title: "Chat Available After Speaker Confirmation",
    description:
      "To ensure personalized and accurate feedback, please confirm who is speaking in this conversation first.",
  },
  summary: {
    icon: BarChart2,
    title: "Summary Available After Speaker Confirmation",
    description:
      "Analysis and therapeutic feedback will be generated once you confirm the speaker identities.",
  },
  comments: {
    icon: MessageCircle,
    title: "Comments Available After Speaker Confirmation",
    description:
      "Inline comments and segment chat will be enabled once you confirm who is speaking.",
  },
};

export function SpeakerConfirmationRequired({
  feature,
  onConfirmSpeakers,
}: SpeakerConfirmationRequiredProps) {
  const config = FEATURE_CONFIG[feature];
  const Icon = config.icon;

  return (
    <Card className="p-8 text-center max-w-md mx-auto">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900 mb-6">
        <Icon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-foreground mb-3">
        {config.title}
      </h3>
      <p className="text-muted-foreground mb-6 leading-relaxed">
        {config.description}
      </p>
      <Button onClick={onConfirmSpeakers} className="gap-2">
        <Users className="w-4 h-4" />
        Confirm Speakers
      </Button>
    </Card>
  );
}
